import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import api from '../../api';
import MobileNavToggle from './MobileNavToggle';
import './platform-stats.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ─── Types ───────────────────────────────────────────────────────────────────

interface ModelUsageRow {
  model: string;
  calls: number;
  pct: number;
  cost: number;
}

interface WeeklyCohortRow {
  cohortWeek: string;
  cohortSize: number;
  retention: number[]; // W0..W12
}

interface ReturnCohort {
  cohortSize: number;
  daily: number[]; // D0..D30
  dailyCohortSizes: number[]; // eligible users per day offset
  weeklyCohorts: WeeklyCohortRow[];
}

interface StatsResponse {
  success: boolean;
  days: string[];
  totalOnboardedUsers: number;
  series: {
    tasks: number[];
    standups: number[];
    calls: number[];
    messages: number[];
    messagesWhatsapp: number[];
    messagesInApp: number[];
    dau: number[];
    mau: number[];
    onboardedUsers: number[];
    standupEnabledUsers: number[];
    sessions: number[];
    sessionsActiveUsers: number[];
  };
  returnCohort: ReturnCohort;
  llmCosts: {
    totalCost: number[];
    avgCostPerCall: number[];
    avgCostPerUser: number[];
    modelUsage: ModelUsageRow[][];
    triggerCosts?: {
      month: Array<{ trigger: string; cost: number }>;
      week: Array<{ trigger: string; cost: number }>;
      day: Array<{ trigger: string; cost: number }>;
    };
  };
  // Email routing outcome breakdown for the "Email Routing" tab. Each
  // window is an array of { bucket, count } pairs sorted by count
  // desc; `bucket` is a server-formatted string like 'filtered',
  // 'subscription_match (user)', 'fallback_routed', 'uncategorized'.
  // Optional because old backends won't return it.
  emailRouting?: {
    month: Array<{ bucket: string; count: number }>;
    week: Array<{ bucket: string; count: number }>;
    day: Array<{ bucket: string; count: number }>;
  };
  // Cost-side companion to `emailRouting` — drives the comparison card
  // and the daily-cost line chart on the Email Routing tab. Both views
  // run server-side over `llm_costs.trigger = 'handleEmail'` so they
  // capture the full cost (intake classifier, lead decision, claim
  // scoring, semantic matcher, agent-mailbox triage, AND every
  // executeThread woken up by an email). Optional because old backends
  // won't return it.
  emailRoutingCost?: {
    todayVsAverage: {
      /** Today's running cost so far (USD). */
      today: number;
      /**
       * Average cost-by-this-time-of-day across the prior N days. The
       * "same elapsed time" anchor matters — comparing today's noon
       * spend to a full-day average would always look low until midnight.
       */
      priorAverage: number;
      /** Number of prior days that contributed to `priorAverage`. */
      priorDays: number;
    };
    /** 30-day daily-cost series (zero-filled). Today is the last entry. */
    daily: Array<{ date: string; cost: number }>;
  };
}

interface CohortRow {
  day: string;
  userId: string;
  userName: string | null;
  companyName: string | null;
  messages: number;
  whatsappMessages: number;
  tasks: number;
  calls: number;
  sessions: number;
}

interface UserStatsResponse {
  success: boolean;
  user: { id: string; name: string | null; companyName: string | null };
  days: string[];
  series: {
    messages: number[];
    tasks: number[];
    calls: number[];
    sessions: number[];
  };
}

type OutcomeKind =
  | 'delivered'
  | 'partial_delivery'
  | 'informed_unable'
  | 'failed'
  | 'cancelled'
  | 'unclassified'
  | 'waiting_on_user'
  | 'waiting_on_third_party'
  | 'in_flight';

interface TaskOutcomesResponse {
  success: boolean;
  days: string[];
  series: Record<OutcomeKind, number[]>;
  totals: {
    classified: number;
    unclassified: number;
    delivered: number;
    partialDelivery: number;
    informedUnable: number;
    failed: number;
    cancelled: number;
    waitingOnUser: number;
    waitingOnThirdParty: number;
    inFlight: number;
    deliveryRate: number | null;
  };
  durations: Array<{
    outcomeKind: string;
    p50Minutes: number;
    p90Minutes: number;
    count: number;
  }>;
}

// Outcome kinds that represent "terminal" states (complete / failed /
// cancelled_by_user). The breakdown table and delivery-rate denominator
// only consider these.
const TERMINAL_OUTCOMES: OutcomeKind[] = [
  'delivered',
  'partial_delivery',
  'informed_unable',
  'cancelled',
  'failed',
];

// Outcome kinds that represent non-terminal, still-alive tasks. These
// indicate possible issues (stuck awaiting user, abandoned in flight)
// and are surfaced as separate tiles + buckets.
const NON_TERMINAL_BUCKETS: OutcomeKind[] = [
  'waiting_on_user',
  'waiting_on_third_party',
  'in_flight',
];

// Response shape from `/platform-stats/task-outcomes/drill-down`. Themes
// are null when the backend skipped clustering (delivered / unclassified
// / non-terminal buckets), or when clustering was attempted but failed.
interface DrillDownTheme {
  name: string;
  description: string;
  threadIds: string[];
}

interface DrillDownRow {
  threadId: string;
  userName: string | null;
  startedAt: string;
  endedAt: string;
  themes: string[];
}

interface DrillDownResponse {
  success: boolean;
  outcomeKind: OutcomeKind;
  windowDays: number;
  total: number;
  rows: DrillDownRow[];
  themes: DrillDownTheme[] | null;
  themesSkipped: boolean;
}

// Colour + label for each outcome kind. Ordered from best → worst so the
// stacked bar reads top-to-bottom in order of user satisfaction, ending
// with "still alive" buckets that are parked or in flight.
const OUTCOME_ORDER: OutcomeKind[] = [
  ...TERMINAL_OUTCOMES,
  'unclassified',
  ...NON_TERMINAL_BUCKETS,
];

const OUTCOME_META: Record<
  OutcomeKind,
  { label: string; color: string; description: string }
> = {
  delivered: {
    label: 'Delivered',
    color: 'rgba(34, 197, 94, 0.85)',
    description: 'User got what they asked for, in full',
  },
  partial_delivery: {
    label: 'Partial',
    color: 'rgba(132, 204, 22, 0.85)',
    description: 'Some but not all of the request was delivered',
  },
  informed_unable: {
    label: 'Informed unable',
    color: 'rgba(250, 204, 21, 0.85)',
    description: 'Honestly reported back that it could not be done',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'rgba(156, 163, 175, 0.85)',
    description: 'User cancelled before completion',
  },
  failed: {
    label: 'Failed',
    color: 'rgba(239, 68, 68, 0.85)',
    description: 'Technical failure or agent gave up mid-task',
  },
  unclassified: {
    label: 'Unclassified',
    color: 'rgba(209, 213, 219, 0.55)',
    description:
      'Terminal task that never received an outcome (agent did not self-report; may indicate a crash)',
  },
  waiting_on_user: {
    label: 'Waiting on user',
    color: 'rgba(59, 130, 246, 0.75)',
    description:
      'Parked awaiting a user response — long-lived rows here may indicate the ask went unnoticed',
  },
  waiting_on_third_party: {
    label: 'Waiting on 3rd party',
    color: 'rgba(168, 85, 247, 0.75)',
    description:
      'Waiting on an external party (e.g. a human task on the Dream team)',
  },
  in_flight: {
    label: 'In flight',
    color: 'rgba(14, 165, 233, 0.75)',
    description:
      'Still processing — if this persists, the task is effectively abandoned and may need intervention',
  },
};

interface AiCallsStatsResponse {
  success: boolean;
  days: string[];
  categoryGroupSeries: Record<string, number[]>;
  assessmentCategories: Array<{ category: string; count: number }>;
  responseLatencies: number[];
  totals: {
    totalCalls: number;
    connectedCalls: number;
    assessedCalls: number;
    connectionRate: number | null;
    successRate: number | null;
    successCount: number;
  };
}

const DAY_OPTIONS = [7, 14, 30, 60, 90];
type Tab =
  | 'charts'
  | 'cohorts'
  | 'outcomes'
  | 'costs'
  | 'email-routing'
  | 'ai-calls';

// ─── Outcome date range ─────────────────────────────────────────────────────

/**
 * Discriminated union representing the active window for the Outcomes
 * tab. Preset is the standard rolling-window mode (last N days). Custom
 * lets the user compare specific calendar windows ("last week" vs "this
 * week", etc.) — the backend resolves either form via `resolveDateRange`.
 */
type DateRange =
  | { mode: 'preset'; days: number }
  | { mode: 'custom'; start: string; end: string };

const formatDateInput = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const today = () => formatDateInput(new Date());
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return formatDateInput(d);
};

/** Build the query string fragment for the outcome endpoints. */
const rangeToQuery = (r: DateRange): string =>
  r.mode === 'preset'
    ? `days=${r.days}`
    : `startDate=${encodeURIComponent(r.start)}&endDate=${encodeURIComponent(r.end)}`;

/** Human-readable label for tooltips and the drill-down header. */
const rangeLabel = (r: DateRange): string => {
  if (r.mode === 'preset')
    return `last ${r.days} day${r.days === 1 ? '' : 's'}`;
  return `${r.start} → ${r.end}`;
};

// ─── Chart helpers ───────────────────────────────────────────────────────────

const formatLabel = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
};

const formatUSD = (v: number) => {
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(3)}`;
  if (v > 0) return `$${v.toFixed(4)}`;
  return '$0.00';
};

const barChartOptions = (title: string) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      text: title,
      font: { family: 'Geist, sans-serif', size: 14, weight: 600 as const },
      color: '#222',
      padding: { bottom: 12 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { family: 'Geist, sans-serif', size: 11 },
        color: '#888',
        maxRotation: 45,
        autoSkip: true,
        maxTicksLimit: 15,
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#f0f0f0' },
      ticks: {
        font: { family: 'Geist, sans-serif', size: 11 },
        color: '#888',
        precision: 0,
      },
    },
  },
});

const lineChartOptions = (title: string) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: 'top' as const },
    title: {
      display: true,
      text: title,
      font: { family: 'Geist, sans-serif', size: 14, weight: 600 as const },
      color: '#222',
      padding: { bottom: 12 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { family: 'Geist, sans-serif', size: 11 },
        color: '#888',
        maxRotation: 45,
        autoSkip: true,
        maxTicksLimit: 15,
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#f0f0f0' },
      ticks: {
        font: { family: 'Geist, sans-serif', size: 11 },
        color: '#888',
        precision: 0,
      },
    },
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

const PlatformStats: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StatsResponse | null>(null);
  const [tab, setTab] = useState<Tab>('charts');

  // Single shared date range state used by ALL tabs (Charts, User
  // Activity, Outcomes). The active window is either a rolling preset
  // (last N days) or an explicit calendar range — backend
  // `resolveDateRange` accepts both forms. `customStart` / `customEnd`
  // back the two date inputs and only get committed to `range` when
  // both are non-empty and ordered, so partial typing doesn't fire
  // half-baked fetches.
  const [range, setRange] = useState<DateRange>({
    mode: 'preset',
    days: 30,
  });
  const [customStart, setCustomStart] = useState<string>(daysAgo(7));
  const [customEnd, setCustomEnd] = useState<string>(today());
  const [perUser, setPerUser] = useState(false);
  const [retentionView, setRetentionView] = useState<'daily' | 'weekly'>(
    'daily',
  );

  // Cohort state
  const [cohortRows, setCohortRows] = useState<CohortRow[]>([]);
  const [cohortLoading, setCohortLoading] = useState(false);

  // User drill-down state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<UserStatsResponse | null>(null);
  const [userLoading, setUserLoading] = useState(false);

  // Outcomes state
  const [outcomes, setOutcomes] = useState<TaskOutcomesResponse | null>(null);
  const [outcomesLoading, setOutcomesLoading] = useState(false);

  // AI Phone Calls state
  const [aiCallsData, setAiCallsData] = useState<AiCallsStatsResponse | null>(
    null,
  );
  const [aiCallsLoading, setAiCallsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<StatsResponse>(
        `/platform-stats?${rangeToQuery(range)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setData(res.data);
        setHasAccess(true);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHasAccess(false);
      } else {
        setError('Failed to load stats');
      }
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, range]);

  const fetchCohorts = useCallback(async () => {
    setCohortLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{ success: boolean; rows: CohortRow[] }>(
        `/platform-stats/cohorts?${rangeToQuery(range)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setCohortRows(res.data.rows);
    } catch {
      setError('Failed to load cohort data');
    } finally {
      setCohortLoading(false);
    }
  }, [getAccessTokenSilently, range]);

  const fetchUserStats = useCallback(
    async (uid: string) => {
      setUserLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<UserStatsResponse>(
          `/platform-stats/user/${encodeURIComponent(uid)}?${rangeToQuery(range)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) setUserStats(res.data);
      } catch {
        setError('Failed to load user stats');
      } finally {
        setUserLoading(false);
      }
    },
    [getAccessTokenSilently, range],
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (tab === 'cohorts') fetchCohorts();
  }, [tab, fetchCohorts]);

  const fetchOutcomes = useCallback(async () => {
    setOutcomesLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<TaskOutcomesResponse>(
        `/platform-stats/task-outcomes?${rangeToQuery(range)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setOutcomes(res.data);
    } catch {
      setError('Failed to load task outcome data');
    } finally {
      setOutcomesLoading(false);
    }
  }, [getAccessTokenSilently, range]);

  useEffect(() => {
    if (tab === 'outcomes') fetchOutcomes();
  }, [tab, fetchOutcomes]);

  const fetchAiCalls = useCallback(async () => {
    setAiCallsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<AiCallsStatsResponse>(
        `/platform-stats/ai-calls?${rangeToQuery(range)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setAiCallsData(res.data);
    } catch {
      setError('Failed to load AI calls data');
    } finally {
      setAiCallsLoading(false);
    }
  }, [getAccessTokenSilently, range]);

  useEffect(() => {
    if (tab === 'ai-calls') fetchAiCalls();
  }, [tab, fetchAiCalls]);

  useEffect(() => {
    if (selectedUserId) fetchUserStats(selectedUserId);
  }, [selectedUserId, fetchUserStats]);

  // ─── Cohort helpers ────────────────────────────────────────────────────────
  // NOTE: keep this (and any other hooks) ABOVE the access-denied early
  // return. React's rules-of-hooks require the same hook order on every
  // render, so a conditional early return above a hook will blow up with
  // "Rendered fewer hooks than expected" the moment hasAccess flips.
  const cohortByDay = useMemo(() => {
    const map = new Map<string, CohortRow[]>();
    for (const r of cohortRows) {
      const key = r.day;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [cohortRows]);

  if (hasAccess === false) {
    return (
      <div className='platform-stats'>
        <div className='access-denied'>Access denied.</div>
      </div>
    );
  }

  const labels = data?.days.map(formatLabel) ?? [];

  const makeBarData = (values: number[], color: string) => ({
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: color,
        borderRadius: 3,
        barPercentage: 0.7,
      },
    ],
  });

  const dauMauData = {
    labels,
    datasets: [
      {
        label: 'DAU',
        data: data?.series.dau ?? [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
      {
        label: 'MAU (28-day)',
        data: data?.series.mau ?? [],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.08)',
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
    ],
  };

  const handleUserClick = (uid: string) => {
    setSelectedUserId(uid);
    setUserStats(null);
  };

  const userBarLabels = userStats?.days.map(formatLabel) ?? [];
  const makeUserBar = (values: number[], color: string) => ({
    labels: userBarLabels,
    datasets: [
      {
        data: values,
        backgroundColor: color,
        borderRadius: 3,
        barPercentage: 0.7,
      },
    ],
  });

  return (
    <div className='platform-stats'>
      <MobileNavToggle />
      <div className='header'>
        <h2>Platform Stats</h2>
        <div className='tab-selector'>
          <button
            className={`tab-btn ${tab === 'charts' ? 'active' : ''}`}
            onClick={() => setTab('charts')}
          >
            Charts
          </button>
          <button
            className={`tab-btn ${tab === 'cohorts' ? 'active' : ''}`}
            onClick={() => setTab('cohorts')}
          >
            User Activity
          </button>
          <button
            className={`tab-btn ${tab === 'outcomes' ? 'active' : ''}`}
            onClick={() => setTab('outcomes')}
          >
            Outcomes
          </button>
          <button
            className={`tab-btn ${tab === 'costs' ? 'active' : ''}`}
            onClick={() => setTab('costs')}
          >
            Costs
          </button>
          <button
            className={`tab-btn ${tab === 'email-routing' ? 'active' : ''}`}
            onClick={() => setTab('email-routing')}
          >
            Email Routing
          </button>
          <button
            className={`tab-btn ${tab === 'ai-calls' ? 'active' : ''}`}
            onClick={() => setTab('ai-calls')}
          >
            AI Calls
          </button>
        </div>

        {/* Single shared date-range selector. Applies to whichever
            tab is active: Charts, User Activity, and Outcomes all
            consume the same `range` state, so flipping between tabs
            keeps the chosen window. */}
        <div className='day-selector outcome-range-selector'>
          {DAY_OPTIONS.map((d) => {
            const active = range.mode === 'preset' && range.days === d;
            return (
              <button
                key={d}
                className={`day-btn ${active ? 'active' : ''}`}
                onClick={() => setRange({ mode: 'preset', days: d })}
              >
                {d}d
              </button>
            );
          })}
          <button
            className={`day-btn ${range.mode === 'custom' ? 'active' : ''}`}
            onClick={() => {
              // Switch into custom mode using the currently-staged
              // start/end dates. If the staged values are blank or
              // invalid we fall back to a sensible "last 7 days"
              // window so the user always sees data immediately.
              const start =
                customStart && customEnd && customStart <= customEnd
                  ? customStart
                  : daysAgo(7);
              const end =
                customStart && customEnd && customStart <= customEnd
                  ? customEnd
                  : today();
              setCustomStart(start);
              setCustomEnd(end);
              setRange({ mode: 'custom', start, end });
            }}
            title='Pick a custom calendar range'
          >
            Custom
          </button>

          {range.mode === 'custom' && (
            <div className='custom-range-inputs'>
              <input
                type='date'
                className='date-input'
                value={customStart}
                max={customEnd || today()}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomStart(v);
                  if (v && customEnd && v <= customEnd) {
                    setRange({
                      mode: 'custom',
                      start: v,
                      end: customEnd,
                    });
                  }
                }}
                aria-label='Start date'
              />
              <span className='range-arrow'>→</span>
              <input
                type='date'
                className='date-input'
                value={customEnd}
                min={customStart || undefined}
                max={today()}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomEnd(v);
                  if (v && customStart && customStart <= v) {
                    setRange({
                      mode: 'custom',
                      start: customStart,
                      end: v,
                    });
                  }
                }}
                aria-label='End date'
              />
            </div>
          )}
        </div>
      </div>

      {error && <div className='msg error'>{error}</div>}

      {tab === 'charts' && (
        <>
          {loading ? (
            <div className='loading'>Loading...</div>
          ) : data ? (
            <div className='charts-grid'>
              <div className='chart-card wide'>
                <div className='chart-header-row'>
                  <span className='stat-badge'>
                    Total Onboarded: <strong>{data.totalOnboardedUsers}</strong>
                  </span>
                </div>
                <Line
                  data={dauMauData}
                  options={lineChartOptions('DAU / MAU (onboarded users only)')}
                />
              </div>
              <div className='per-user-toggle'>
                <button
                  className={`toggle-btn ${perUser ? 'active' : ''}`}
                  onClick={() => setPerUser((v) => !v)}
                >
                  {perUser ? '# per Onboarded User' : 'Totals'}
                </button>
              </div>
              <div className='chart-card'>
                <Bar
                  data={{
                    labels,
                    datasets: [
                      {
                        label: 'In-App',
                        data: perUser
                          ? data.series.messagesInApp.map((v, i) =>
                              data.series.onboardedUsers[i] > 0
                                ? Math.round(
                                    (v / data.series.onboardedUsers[i]) * 100,
                                  ) / 100
                                : 0,
                            )
                          : data.series.messagesInApp,
                        backgroundColor: 'rgba(99, 102, 241, 0.7)',
                        borderRadius: 3,
                        barPercentage: 0.7,
                      },
                      {
                        label: 'WhatsApp',
                        data: perUser
                          ? data.series.messagesWhatsapp.map((v, i) =>
                              data.series.onboardedUsers[i] > 0
                                ? Math.round(
                                    (v / data.series.onboardedUsers[i]) * 100,
                                  ) / 100
                                : 0,
                            )
                          : data.series.messagesWhatsapp,
                        backgroundColor: 'rgba(37, 211, 102, 0.7)',
                        borderRadius: 3,
                        barPercentage: 0.7,
                      },
                    ],
                  }}
                  options={{
                    ...barChartOptions(
                      perUser ? 'Messages per Onboarded User' : 'Messages Sent',
                    ),
                    plugins: {
                      ...barChartOptions('Messages Sent').plugins,
                      legend: { display: true, position: 'top' as const },
                    },
                    scales: {
                      ...barChartOptions('Messages Sent').scales,
                      x: {
                        ...barChartOptions('Messages Sent').scales.x,
                        stacked: true,
                      },
                      y: {
                        ...barChartOptions('Messages Sent').scales.y,
                        stacked: true,
                      },
                    },
                  }}
                />
              </div>
              <div className='chart-card'>
                <Bar
                  data={makeBarData(
                    perUser
                      ? data.series.tasks.map((v, i) =>
                          data.series.onboardedUsers[i] > 0
                            ? Math.round(
                                (v / data.series.onboardedUsers[i]) * 100,
                              ) / 100
                            : 0,
                        )
                      : data.series.tasks,
                    'rgba(244, 114, 182, 0.7)',
                  )}
                  options={barChartOptions(
                    perUser ? 'Tasks per Onboarded User' : 'Tasks Created',
                  )}
                />
              </div>
              <div className='chart-card'>
                <Bar
                  data={makeBarData(
                    data.series.standups.map((v, i) =>
                      data.series.standupEnabledUsers[i] > 0
                        ? Math.round(
                            (v / data.series.standupEnabledUsers[i]) * 10000,
                          ) / 100
                        : 0,
                    ),
                    'rgba(251, 191, 36, 0.7)',
                  )}
                  options={{
                    ...barChartOptions('Standup Completion %'),
                    scales: {
                      ...barChartOptions('Standup Completion %').scales,
                      y: {
                        ...barChartOptions('Standup Completion %').scales.y,
                        max: 100,
                        ticks: {
                          ...barChartOptions('Standup Completion %').scales.y
                            .ticks,
                          callback: (v: any) => `${v}%`,
                        },
                      },
                    },
                    plugins: {
                      ...barChartOptions('Standup Completion %').plugins,
                      tooltip: {
                        callbacks: {
                          label: (ctx: any) => `${ctx.raw.toFixed(1)}%`,
                        },
                      },
                    },
                  }}
                />
              </div>
              <div className='chart-card'>
                <Bar
                  data={makeBarData(
                    perUser
                      ? data.series.calls.map((v, i) =>
                          data.series.onboardedUsers[i] > 0
                            ? Math.round(
                                (v / data.series.onboardedUsers[i]) * 100,
                              ) / 100
                            : 0,
                        )
                      : data.series.calls,
                    'rgba(34, 197, 94, 0.7)',
                  )}
                  options={barChartOptions(
                    perUser ? 'Phone Calls per Onboarded User' : 'Phone Calls',
                  )}
                />
              </div>
              {data.series.sessions && (
                <div className='chart-card'>
                  <Bar
                    data={makeBarData(
                      perUser
                        ? data.series.sessions.map((v, i) =>
                            (data.series.sessionsActiveUsers?.[i] ?? 0) > 0
                              ? Math.round(
                                  (v / data.series.sessionsActiveUsers[i]) *
                                    100,
                                ) / 100
                              : 0,
                          )
                        : data.series.sessions,
                      'rgba(14, 165, 233, 0.7)',
                    )}
                    options={barChartOptions(
                      perUser
                        ? 'Sessions per Active User'
                        : 'Sessions (30-min gap)',
                    )}
                  />
                </div>
              )}
              {data.llmCosts && (
                <>
                  <div className='chart-card'>
                    <Line
                      data={{
                        labels,
                        datasets: [
                          {
                            label: 'Total Spend',
                            data: data.llmCosts.totalCost,
                            borderColor: 'rgb(239, 68, 68)',
                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 2,
                          },
                        ],
                      }}
                      options={{
                        ...lineChartOptions('Daily LLM Expenditure ($)'),
                        plugins: {
                          ...lineChartOptions('Daily LLM Expenditure ($)')
                            .plugins,
                          tooltip: {
                            callbacks: {
                              label: (ctx: any) =>
                                `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                            },
                          },
                        },
                        scales: {
                          ...lineChartOptions('Daily LLM Expenditure ($)')
                            .scales,
                          y: {
                            ...lineChartOptions('Daily LLM Expenditure ($)')
                              .scales.y,
                            ticks: {
                              ...lineChartOptions('Daily LLM Expenditure ($)')
                                .scales.y.ticks,
                              callback: (v: any) => formatUSD(v),
                            },
                          },
                        },
                      }}
                    />
                  </div>
                  <div className='chart-card'>
                    <Line
                      data={{
                        labels,
                        datasets: [
                          {
                            label: 'Avg per Account',
                            data: data.llmCosts.avgCostPerUser,
                            borderColor: 'rgb(168, 85, 247)',
                            backgroundColor: 'rgba(168, 85, 247, 0.08)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 2,
                          },
                        ],
                      }}
                      options={{
                        ...lineChartOptions('Avg Cost per Account per Day ($)'),
                        plugins: {
                          ...lineChartOptions(
                            'Avg Cost per Account per Day ($)',
                          ).plugins,
                          tooltip: {
                            callbacks: {
                              label: (ctx: any) =>
                                `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                            },
                          },
                        },
                        scales: {
                          ...lineChartOptions(
                            'Avg Cost per Account per Day ($)',
                          ).scales,
                          y: {
                            ...lineChartOptions(
                              'Avg Cost per Account per Day ($)',
                            ).scales.y,
                            ticks: {
                              ...lineChartOptions(
                                'Avg Cost per Account per Day ($)',
                              ).scales.y.ticks,
                              callback: (v: any) => formatUSD(v),
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          ) : null}

          {data?.returnCohort && data.returnCohort.cohortSize > 0 && (
            <div className='return-cohorts-section'>
              <div className='cohort-title-row'>
                <div>
                  <h3>Return Cohort</h3>
                  <p className='cohort-subtitle'>
                    {retentionView === 'daily'
                      ? `% of eligible users active on day N after their signup (${data.returnCohort.cohortSize} total onboarded)`
                      : 'Retention % by onboarding week (messages, tasks, or calls)'}
                  </p>
                </div>
                <div className='retention-toggle'>
                  <button
                    className={`toggle-btn ${retentionView === 'daily' ? 'active' : ''}`}
                    onClick={() => setRetentionView('daily')}
                  >
                    Daily
                  </button>
                  <button
                    className={`toggle-btn ${retentionView === 'weekly' ? 'active' : ''}`}
                    onClick={() => setRetentionView('weekly')}
                  >
                    Weekly
                  </button>
                </div>
              </div>

              {retentionView === 'daily' ? (
                <div className='cohort-heatmap-wrap'>
                  <table className='cohort-heatmap'>
                    <thead>
                      <tr>
                        {data.returnCohort.daily.map((_: number, i: number) => (
                          <th key={i}>D{i}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {data.returnCohort.daily.map(
                          (active: number, i: number) => {
                            const eligible =
                              data.returnCohort.dailyCohortSizes?.[i] ??
                              data.returnCohort.cohortSize;
                            const pct =
                              eligible > 0
                                ? Math.round((active / eligible) * 100)
                                : 0;
                            const opacity = Math.min(pct / 100, 1);
                            return (
                              <td
                                key={i}
                                className='cohort-cell'
                                style={{
                                  backgroundColor: `rgba(59, 130, 246, ${opacity * 0.8 + 0.05})`,
                                  color: opacity > 0.45 ? '#fff' : '#333',
                                }}
                                title={`${active}/${eligible} eligible (${pct}%)`}
                              >
                                {pct > 0 ? `${pct}%` : '–'}
                              </td>
                            );
                          },
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className='cohort-heatmap-wrap'>
                  <table className='cohort-heatmap'>
                    <thead>
                      <tr>
                        <th>Cohort</th>
                        <th>Size</th>
                        {Array.from({ length: 13 }, (_, i) => (
                          <th key={i}>W{i}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.returnCohort.weeklyCohorts.map((c) => (
                        <tr key={c.cohortWeek}>
                          <td className='cohort-week-label'>{c.cohortWeek}</td>
                          <td className='cohort-size'>{c.cohortSize}</td>
                          {c.retention.map((active: number, wi: number) => {
                            const pct =
                              c.cohortSize > 0
                                ? Math.round((active / c.cohortSize) * 100)
                                : 0;
                            const opacity = Math.min(pct / 100, 1);
                            return (
                              <td
                                key={wi}
                                className='cohort-cell'
                                style={{
                                  backgroundColor: `rgba(59, 130, 246, ${opacity * 0.8 + 0.05})`,
                                  color: opacity > 0.45 ? '#fff' : '#333',
                                }}
                                title={`${active}/${c.cohortSize} (${pct}%)`}
                              >
                                {pct > 0 ? `${pct}%` : '–'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'cohorts' && (
        <div className='cohort-section'>
          {cohortLoading ? (
            <div className='loading'>Loading cohort data...</div>
          ) : (
            <div className='cohort-layout'>
              <div className='cohort-table-wrap'>
                {cohortByDay.map(([day, users]: [string, CohortRow[]]) => (
                  <div key={day} className='cohort-day-group'>
                    <div className='cohort-day-header'>
                      <span className='cohort-day-label'>
                        {formatLabel(day)}
                      </span>
                      <span className='cohort-day-count'>
                        {users.length} user{users.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <table className='cohort-table'>
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Company</th>
                          <th>Messages</th>
                          <th>% WA</th>
                          <th>Tasks</th>
                          <th>Calls</th>
                          <th>Sessions</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u: CohortRow) => (
                          <tr
                            key={u.userId}
                            className={`cohort-row ${selectedUserId === u.userId ? 'selected' : ''}`}
                            onClick={() => handleUserClick(u.userId)}
                          >
                            <td className='user-name'>
                              {u.userName || u.userId?.slice(0, 12) || '–'}
                            </td>
                            <td className='company-name'>
                              {u.companyName || '–'}
                            </td>
                            <td>{u.messages}</td>
                            <td>
                              {u.messages > 0
                                ? `${Math.round((u.whatsappMessages / u.messages) * 100)}%`
                                : '–'}
                            </td>
                            <td>{u.tasks}</td>
                            <td>{u.calls}</td>
                            <td>{u.sessions}</td>
                            <td className='total'>
                              {u.messages + u.tasks + u.calls}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              {selectedUserId && (
                <div className='user-detail-panel'>
                  <div className='user-detail-header'>
                    <h3>
                      {userStats?.user.name ||
                        selectedUserId?.slice(0, 16) ||
                        '–'}
                    </h3>
                    {userStats?.user.companyName && (
                      <span className='user-detail-company'>
                        {userStats.user.companyName}
                      </span>
                    )}
                    <button
                      className='close-btn'
                      onClick={() => {
                        setSelectedUserId(null);
                        setUserStats(null);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  {userLoading ? (
                    <div className='loading'>Loading...</div>
                  ) : userStats ? (
                    <div className='user-charts'>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.messages,
                            'rgba(99, 102, 241, 0.7)',
                          )}
                          options={barChartOptions('Messages')}
                        />
                      </div>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.tasks,
                            'rgba(244, 114, 182, 0.7)',
                          )}
                          options={barChartOptions('Tasks')}
                        />
                      </div>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.calls,
                            'rgba(34, 197, 94, 0.7)',
                          )}
                          options={barChartOptions('Calls')}
                        />
                      </div>
                      <div className='user-chart-card'>
                        <Bar
                          data={makeUserBar(
                            userStats.series.sessions,
                            'rgba(14, 165, 233, 0.7)',
                          )}
                          options={barChartOptions('Sessions')}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'outcomes' && (
        <div className='outcomes-section'>
          {outcomesLoading ? (
            <div className='loading'>Loading outcome data...</div>
          ) : outcomes ? (
            <OutcomesView outcomes={outcomes} dateRange={range} />
          ) : (
            <div className='loading'>No data.</div>
          )}
        </div>
      )}

      {tab === 'email-routing' && (
        <div className='costs-section'>
          {loading ? (
            <div className='loading'>Loading email routing data...</div>
          ) : data?.emailRouting ? (
            <EmailRoutingView
              data={data.emailRouting}
              cost={data.emailRoutingCost}
            />
          ) : (
            <div className='loading'>No email routing data available.</div>
          )}
        </div>
      )}

      {tab === 'costs' && (
        <div className='costs-section'>
          {loading ? (
            <div className='loading'>Loading cost data...</div>
          ) : data?.llmCosts ? (
            <CostsView data={data} />
          ) : (
            <div className='loading'>No cost data available.</div>
          )}
        </div>
      )}

      {tab === 'ai-calls' && (
        <div className='ai-calls-section'>
          {aiCallsLoading ? (
            <div className='loading'>Loading AI phone call data...</div>
          ) : aiCallsData ? (
            <AiCallsView data={aiCallsData} />
          ) : (
            <div className='loading'>No AI call data available.</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Outcomes view ─────────────────────────────────────────────────────────

const formatPct = (v: number | null) =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

const formatMinutes = (m: number) => {
  if (!Number.isFinite(m) || m <= 0) return '—';
  if (m < 1) return `${Math.round(m * 60)}s`;
  if (m < 60) return `${m.toFixed(1)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

interface OutcomesViewProps {
  outcomes: TaskOutcomesResponse;
  /** Window passed through to the drill-down endpoint so it queries the same range. */
  dateRange: DateRange;
}

const OutcomesView: FC<OutcomesViewProps> = ({
  outcomes,
  dateRange: range,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  // Drill-down modal state. `selectedKind` doubles as the "modal open"
  // flag: null = closed. We deliberately reset `drill` whenever the
  // selected kind changes so a stale payload from a previous bucket
  // can't flash for a frame.
  const [selectedKind, setSelectedKind] = useState<OutcomeKind | null>(null);
  const [drill, setDrill] = useState<DrillDownResponse | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);

  const openDrillDown = useCallback(
    async (kind: OutcomeKind) => {
      setSelectedKind(kind);
      setDrill(null);
      setDrillError(null);
      setDrillLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<DrillDownResponse>(
          `/platform-stats/task-outcomes/drill-down?outcomeKind=${kind}&${rangeToQuery(range)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) setDrill(res.data);
        else setDrillError('Failed to load drill-down');
      } catch {
        setDrillError('Failed to load drill-down');
      } finally {
        setDrillLoading(false);
      }
    },
    [getAccessTokenSilently, range],
  );

  const closeDrillDown = useCallback(() => {
    setSelectedKind(null);
    setDrill(null);
    setDrillError(null);
    setDrillLoading(false);
  }, []);

  // ESC closes the modal. We attach the listener only while the modal
  // is open so we don't fight other ESC handlers in the page.
  useEffect(() => {
    if (!selectedKind) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrillDown();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedKind, closeDrillDown]);

  const labels = outcomes.days.map(formatLabel);

  // Stacked bar: one dataset per outcome kind, in visual order.
  const stackedData = {
    labels,
    datasets: OUTCOME_ORDER.map((kind) => ({
      label: OUTCOME_META[kind].label,
      data: outcomes.series[kind] ?? [],
      backgroundColor: OUTCOME_META[kind].color,
      borderRadius: 2,
      barPercentage: 0.8,
      stack: 'outcomes',
    })),
  };

  const stackedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          font: { family: 'Geist, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: {
        display: true,
        text: 'Outcomes per day',
        font: { family: 'Geist, sans-serif', size: 14, weight: 600 as const },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#888',
          precision: 0,
        },
      },
    },
  };

  const totalTerminal =
    outcomes.totals.classified + outcomes.totals.unclassified;
  const stuckTotal =
    outcomes.totals.waitingOnUser +
    outcomes.totals.waitingOnThirdParty +
    outcomes.totals.inFlight;
  const windowTotal = totalTerminal + stuckTotal;

  const pctOf = (count: number) =>
    outcomes.totals.classified > 0
      ? `${((count / outcomes.totals.classified) * 100).toFixed(1)}%`
      : '—';

  // Group durations by outcome for quick lookup.
  const durationsByKind = new Map(
    outcomes.durations.map((d) => [d.outcomeKind, d]),
  );

  // Counts keyed by OutcomeKind so the table rows can look them up
  // without the long ternary chain.
  const countByKind: Record<OutcomeKind, number> = {
    delivered: outcomes.totals.delivered,
    partial_delivery: outcomes.totals.partialDelivery,
    informed_unable: outcomes.totals.informedUnable,
    failed: outcomes.totals.failed,
    cancelled: outcomes.totals.cancelled,
    unclassified: outcomes.totals.unclassified,
    waiting_on_user: outcomes.totals.waitingOnUser,
    waiting_on_third_party: outcomes.totals.waitingOnThirdParty,
    in_flight: outcomes.totals.inFlight,
  };

  return (
    <div className='outcomes-view'>
      <div className='outcomes-summary'>
        <div className='summary-tile primary'>
          <div className='tile-label'>Delivery rate</div>
          <div className='tile-value'>
            {formatPct(outcomes.totals.deliveryRate)}
          </div>
          <div className='tile-sub'>
            Partial counts as ½. Based on {outcomes.totals.classified}{' '}
            classified task{outcomes.totals.classified === 1 ? '' : 's'}.
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Unclassified</div>
          <div className='tile-value'>{outcomes.totals.unclassified}</div>
          <div className='tile-sub'>
            terminal but no outcome reported
            {totalTerminal > 0
              ? ` (${((outcomes.totals.unclassified / totalTerminal) * 100).toFixed(0)}%)`
              : ''}
          </div>
        </div>
        <div className='summary-tile warn'>
          <div className='tile-label'>Still alive</div>
          <div className='tile-value'>{stuckTotal}</div>
          <div className='tile-sub'>
            {outcomes.totals.waitingOnUser} waiting on user ·{' '}
            {outcomes.totals.waitingOnThirdParty} on 3rd party ·{' '}
            {outcomes.totals.inFlight} in flight
          </div>
        </div>
      </div>

      <div className='chart-card wide outcomes-chart'>
        <Bar data={stackedData} options={stackedOptions} />
      </div>

      <div className='outcomes-breakdown'>
        <div className='breakdown-title'>
          Terminal outcomes (classified share of {outcomes.totals.classified})
        </div>
        <table className='outcomes-table'>
          <thead>
            <tr>
              <th>Outcome</th>
              <th>Count</th>
              <th>Share</th>
              <th>P50 duration</th>
              <th>P90 duration</th>
              <th className='desc-col'>What it means</th>
            </tr>
          </thead>
          <tbody>
            {TERMINAL_OUTCOMES.map((kind) => {
              const meta = OUTCOME_META[kind];
              const count = countByKind[kind];
              const dur = durationsByKind.get(kind);
              const clickable = count > 0;
              return (
                <tr
                  key={kind}
                  className={clickable ? 'clickable' : ''}
                  onClick={clickable ? () => openDrillDown(kind) : undefined}
                  title={clickable ? `Drill into ${meta.label}` : undefined}
                >
                  <td className='outcome-cell'>
                    <span
                      className='outcome-dot'
                      style={{ background: meta.color }}
                    />
                    {meta.label}
                  </td>
                  <td>{count}</td>
                  <td>{pctOf(count)}</td>
                  <td>{dur ? formatMinutes(dur.p50Minutes) : '—'}</td>
                  <td>{dur ? formatMinutes(dur.p90Minutes) : '—'}</td>
                  <td className='desc-col'>{meta.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className='outcomes-breakdown'>
        <div className='breakdown-title'>
          Non-terminal & unreported ({stuckTotal + countByKind.unclassified} of{' '}
          {windowTotal})
        </div>
        <table className='outcomes-table'>
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
              <th>Share of window</th>
              <th className='desc-col'>What it means</th>
            </tr>
          </thead>
          <tbody>
            {[...NON_TERMINAL_BUCKETS, 'unclassified' as const].map((kind) => {
              const meta = OUTCOME_META[kind];
              const count = countByKind[kind];
              const share =
                windowTotal > 0
                  ? `${((count / windowTotal) * 100).toFixed(1)}%`
                  : '—';
              const clickable = count > 0;
              return (
                <tr
                  key={kind}
                  className={clickable ? 'clickable' : ''}
                  onClick={clickable ? () => openDrillDown(kind) : undefined}
                  title={clickable ? `Drill into ${meta.label}` : undefined}
                >
                  <td className='outcome-cell'>
                    <span
                      className='outcome-dot'
                      style={{ background: meta.color }}
                    />
                    {meta.label}
                  </td>
                  <td>{count}</td>
                  <td>{share}</td>
                  <td className='desc-col'>{meta.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedKind && (
        <DrillDownModal
          outcomeKind={selectedKind}
          windowLabel={rangeLabel(range)}
          loading={drillLoading}
          error={drillError}
          data={drill}
          onClose={closeDrillDown}
        />
      )}
    </div>
  );
};

// ─── Drill-down modal ──────────────────────────────────────────────────────

interface DrillDownModalProps {
  outcomeKind: OutcomeKind;
  /** Human-readable description of the active window, e.g. "last 30 days" or "2026-04-15 → 2026-04-22". */
  windowLabel: string;
  loading: boolean;
  error: string | null;
  data: DrillDownResponse | null;
  onClose: () => void;
}

const DrillDownModal: FC<DrillDownModalProps> = ({
  outcomeKind,
  windowLabel,
  loading,
  error,
  data,
  onClose,
}) => {
  const meta = OUTCOME_META[outcomeKind];

  // Format ISO timestamp into "DD MMM, HH:mm" — short but unambiguous.
  const formatDateTime = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${date}, ${time}`;
  };

  // Format duration between two ISO timestamps in human terms.
  const formatRange = (startIso: string, endIso: string) => {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
      return '—';
    const minutes = (end - start) / 60_000;
    return formatMinutes(minutes);
  };

  return (
    <div
      className='drill-down-overlay'
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className='drill-down-panel'
        role='dialog'
        aria-modal='true'
        aria-label={`Drill-down for ${meta.label}`}
      >
        <div className='drill-down-header'>
          <div className='drill-down-title'>
            <span className='outcome-dot' style={{ background: meta.color }} />
            <span>{meta.label}</span>
            <span className='drill-down-window'>· {windowLabel}</span>
          </div>
          <button
            type='button'
            className='drill-down-close'
            onClick={onClose}
            aria-label='Close drill-down'
          >
            ×
          </button>
        </div>

        {loading && (
          <div className='drill-down-loading'>
            Loading drill-down… theme classification can take 5–15 s.
          </div>
        )}

        {error && !loading && <div className='drill-down-error'>{error}</div>}

        {data && !loading && (
          <>
            <div className='drill-down-summary'>
              {data.total} matching task{data.total === 1 ? '' : 's'}
              {data.total >= 500 ? ' (capped at 500 most-recent)' : ''}.
            </div>

            {/* Themes section — only rendered when the backend actually
                ran clustering. For delivered / non-terminal / unclassified
                buckets the backend short-circuits and we show a helper
                blurb in place of an empty themes list. */}
            {data.themesSkipped ? (
              <div className='drill-down-themes-skipped'>
                Theme classification is skipped for this bucket — it adds little
                signal here. Use the row list below instead.
              </div>
            ) : data.themes && data.themes.length > 0 ? (
              <div className='drill-down-themes'>
                <div className='drill-down-section-title'>
                  Themes (LLM-generated)
                </div>
                <ul className='drill-down-theme-list'>
                  {data.themes.map((t) => (
                    <li key={t.name}>
                      <div className='theme-name'>
                        {t.name}{' '}
                        <span className='theme-count'>
                          ({t.threadIds.length})
                        </span>
                      </div>
                      <div className='theme-description'>{t.description}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : data.total > 0 ? (
              <div className='drill-down-themes-skipped'>
                Theme classification did not return results.
              </div>
            ) : null}

            {/* Row list — privacy-conscious: no descriptions / summaries. */}
            <div className='drill-down-rows'>
              <div className='drill-down-section-title'>Tasks</div>
              {data.rows.length === 0 ? (
                <div className='drill-down-empty'>
                  No tasks in this bucket for the selected window.
                </div>
              ) : (
                <table className='drill-down-table'>
                  <thead>
                    <tr>
                      <th>Thread</th>
                      <th>User</th>
                      <th>Started</th>
                      <th>Ended</th>
                      <th>Duration</th>
                      <th>Themes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={row.threadId}>
                        <td className='mono thread-id-cell'>
                          <span
                            className='thread-id'
                            title='Click to copy'
                            onClick={() => {
                              if (navigator.clipboard?.writeText) {
                                navigator.clipboard
                                  .writeText(row.threadId)
                                  .catch(() => {});
                              }
                            }}
                          >
                            {row.threadId}
                          </span>
                        </td>
                        <td>{row.userName || '—'}</td>
                        <td>{formatDateTime(row.startedAt)}</td>
                        <td>{formatDateTime(row.endedAt)}</td>
                        <td>{formatRange(row.startedAt, row.endedAt)}</td>
                        <td>
                          {row.themes.length > 0 ? (
                            <div className='theme-tags'>
                              {row.themes.map((tn) => (
                                <span key={tn} className='theme-tag'>
                                  {tn}
                                </span>
                              ))}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Costs view ───────────────────────────────────────────────────────────────

// Stable colour palette for model names. Hues are spread evenly around the
// wheel so they stay distinguishable even with 5+ models.
const MODEL_COLORS = [
  'rgba(239, 68, 68, 0.75)', // red
  'rgba(59, 130, 246, 0.75)', // blue
  'rgba(34, 197, 94, 0.75)', // green
  'rgba(168, 85, 247, 0.75)', // purple
  'rgba(245, 158, 11, 0.75)', // amber
  'rgba(14, 165, 233, 0.75)', // sky
  'rgba(236, 72, 153, 0.75)', // pink
  'rgba(20, 184, 166, 0.75)', // teal
];

interface CostsViewProps {
  data: StatsResponse;
}

const CostsView: FC<CostsViewProps> = ({ data }) => {
  const { days, series, llmCosts } = data;
  const labels = days.map(formatLabel);

  // ── Aggregate model stats across the entire window ──────────────────────
  const modelTotals = useMemo(() => {
    const map = new Map<string, { calls: number; cost: number }>();
    for (const dayModels of llmCosts.modelUsage) {
      for (const m of dayModels) {
        const prev = map.get(m.model) ?? { calls: 0, cost: 0 };
        map.set(m.model, {
          calls: prev.calls + m.calls,
          cost: prev.cost + m.cost,
        });
      }
    }
    return Array.from(map.entries())
      .map(([model, stats]) => ({ model, ...stats }))
      .sort((a, b) => b.cost - a.cost);
  }, [llmCosts.modelUsage]);

  const windowTotalCost = llmCosts.totalCost.reduce((a, b) => a + b, 0);
  const windowTotalCalls = modelTotals.reduce((a, m) => a + m.calls, 0);
  const windowTotalTasks = series.tasks.reduce((a, b) => a + b, 0);
  const avgCostPerTask =
    windowTotalTasks > 0 ? windowTotalCost / windowTotalTasks : 0;
  const avgCostPerCall =
    windowTotalCalls > 0 ? windowTotalCost / windowTotalCalls : 0;

  // ── Per-model daily cost series for stacked area chart ─────────────────
  const modelNames = modelTotals.map((m) => m.model);
  const modelColorMap = new Map(
    modelNames.map((n, i) => [n, MODEL_COLORS[i % MODEL_COLORS.length]]),
  );

  const modelCostByDay = useMemo(() => {
    const result: Record<string, number[]> = {};
    for (const name of modelNames) {
      result[name] = new Array(days.length).fill(0);
    }
    for (let i = 0; i < days.length; i++) {
      for (const m of llmCosts.modelUsage[i] ?? []) {
        if (result[m.model]) {
          result[m.model][i] = m.cost;
        }
      }
    }
    return result;
  }, [days, llmCosts.modelUsage, modelNames]);

  const stackedCostData = {
    labels,
    datasets: modelNames.map((name) => ({
      label: name,
      data: modelCostByDay[name] ?? [],
      backgroundColor: modelColorMap.get(name) ?? MODEL_COLORS[0],
      borderColor: (modelColorMap.get(name) ?? MODEL_COLORS[0]).replace(
        '0.75',
        '1',
      ),
      fill: true,
      tension: 0.3,
      pointRadius: 0,
      stack: 'cost',
    })),
  };

  const stackedCostOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          font: { family: 'Geist, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: {
        display: true,
        text: 'Daily Cost by Model ($)',
        font: { family: 'Geist, sans-serif', size: 14, weight: 600 as const },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#888',
          callback: (v: any) => formatUSD(v),
        },
      },
    },
  };

  // Avg cost per task per day
  const avgCostPerTaskPerDay = days.map((_, i) =>
    series.tasks[i] > 0 ? llmCosts.totalCost[i] / series.tasks[i] : 0,
  );

  return (
    <div className='costs-view'>
      {/* Summary tiles */}
      <div className='costs-summary'>
        <div className='summary-tile'>
          <div className='tile-label'>Total Spend (window)</div>
          <div className='tile-value'>{formatUSD(windowTotalCost)}</div>
          <div className='tile-sub'>
            {windowTotalCalls.toLocaleString()} LLM calls
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Avg Cost per Task</div>
          <div className='tile-value'>{formatUSD(avgCostPerTask)}</div>
          <div className='tile-sub'>
            {windowTotalTasks.toLocaleString()} tasks in window
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Avg Cost per LLM Call</div>
          <div className='tile-value'>{formatUSD(avgCostPerCall)}</div>
        </div>
      </div>

      {/* Trigger cost pie charts */}
      {llmCosts.triggerCosts && (
        <div className='costs-trigger-pies'>
          {(
            [
              { label: 'Past Month', data: llmCosts.triggerCosts.month },
              { label: 'Past Week', data: llmCosts.triggerCosts.week },
              { label: 'Past Day', data: llmCosts.triggerCosts.day },
            ] as const
          ).map(({ label, data: slices }) => {
            const total = slices.reduce((s, r) => s + r.cost, 0);
            return (
              <div className='costs-chart-card pie-card' key={label}>
                <Pie
                  data={{
                    labels: slices.map((s) => s.trigger),
                    datasets: [
                      {
                        data: slices.map((s) => s.cost),
                        backgroundColor: [
                          ...MODEL_COLORS,
                          'rgba(100, 116, 139, 0.75)',
                          'rgba(251, 146, 60, 0.75)',
                          'rgba(99, 102, 241, 0.75)',
                        ],
                        borderWidth: 1,
                        borderColor: '#fff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      title: {
                        display: true,
                        text: `${label} — ${formatUSD(total)}`,
                        font: {
                          family: 'Geist, sans-serif',
                          size: 14,
                          weight: 600 as const,
                        },
                        color: '#222',
                        padding: { bottom: 8 },
                      },
                      legend: {
                        position: 'bottom' as const,
                        labels: {
                          font: { family: 'Geist, sans-serif', size: 10 },
                          boxWidth: 10,
                          padding: 6,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx: any) => {
                            const val = ctx.raw as number;
                            const pct =
                              total > 0
                                ? ((val / total) * 100).toFixed(1)
                                : '0';
                            return `${ctx.label}: ${formatUSD(val)} (${pct}%)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Stacked area: cost by model */}
      <div className='costs-chart-card wide'>
        <Line data={stackedCostData} options={stackedCostOptions} />
      </div>

      {/* Avg cost per task per day */}
      <div className='costs-chart-card'>
        <Line
          data={{
            labels,
            datasets: [
              {
                label: 'Avg Cost per Task',
                data: avgCostPerTaskPerDay,
                borderColor: 'rgb(244, 114, 182)',
                backgroundColor: 'rgba(244, 114, 182, 0.08)',
                fill: true,
                tension: 0.35,
                pointRadius: 2,
              },
            ],
          }}
          options={{
            ...lineChartOptions('Avg Cost per Task per Day ($)'),
            plugins: {
              ...lineChartOptions('Avg Cost per Task per Day ($)').plugins,
              tooltip: {
                callbacks: {
                  label: (ctx: any) =>
                    `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                },
              },
            },
            scales: {
              ...lineChartOptions('Avg Cost per Task per Day ($)').scales,
              y: {
                ...lineChartOptions('Avg Cost per Task per Day ($)').scales.y,
                ticks: {
                  ...lineChartOptions('Avg Cost per Task per Day ($)').scales.y
                    .ticks,
                  callback: (v: any) => formatUSD(v),
                },
              },
            },
          }}
        />
      </div>

      {/* Avg cost per account per day */}
      <div className='costs-chart-card'>
        <Line
          data={{
            labels,
            datasets: [
              {
                label: 'Avg per Account',
                data: llmCosts.avgCostPerUser,
                borderColor: 'rgb(168, 85, 247)',
                backgroundColor: 'rgba(168, 85, 247, 0.08)',
                fill: true,
                tension: 0.35,
                pointRadius: 2,
              },
            ],
          }}
          options={{
            ...lineChartOptions('Avg Cost per Account per Day ($)'),
            plugins: {
              ...lineChartOptions('Avg Cost per Account per Day ($)').plugins,
              tooltip: {
                callbacks: {
                  label: (ctx: any) =>
                    `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
                },
              },
            },
            scales: {
              ...lineChartOptions('Avg Cost per Account per Day ($)').scales,
              y: {
                ...lineChartOptions('Avg Cost per Account per Day ($)').scales
                  .y,
                ticks: {
                  ...lineChartOptions('Avg Cost per Account per Day ($)').scales
                    .y.ticks,
                  callback: (v: any) => formatUSD(v),
                },
              },
            },
          }}
        />
      </div>

      {/* Model breakdown table */}
      <div className='costs-breakdown'>
        <div className='breakdown-title'>Model Breakdown (Window Total)</div>
        <table className='costs-table'>
          <thead>
            <tr>
              <th>Model</th>
              <th>Calls</th>
              <th>Total Cost</th>
              <th>% of Spend</th>
              <th>Avg per Call</th>
            </tr>
          </thead>
          <tbody>
            {modelTotals.map((m) => (
              <tr key={m.model}>
                <td className='model-cell'>
                  <span
                    className='outcome-dot'
                    style={{ background: modelColorMap.get(m.model) }}
                  />
                  {m.model}
                </td>
                <td>{m.calls.toLocaleString()}</td>
                <td>{formatUSD(m.cost)}</td>
                <td>
                  {windowTotalCost > 0
                    ? `${((m.cost / windowTotalCost) * 100).toFixed(1)}%`
                    : '—'}
                </td>
                <td>{m.calls > 0 ? formatUSD(m.cost / m.calls) : '—'}</td>
              </tr>
            ))}
            {modelTotals.length > 1 && (
              <tr className='totals-row'>
                <td>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>{windowTotalCalls.toLocaleString()}</strong>
                </td>
                <td>
                  <strong>{formatUSD(windowTotalCost)}</strong>
                </td>
                <td>
                  <strong>100%</strong>
                </td>
                <td>
                  <strong>
                    {windowTotalCalls > 0
                      ? formatUSD(windowTotalCost / windowTotalCalls)
                      : '—'}
                  </strong>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── AI Calls view ──────────────────────────────────────────────────────────

const CATEGORY_GROUP_META: Record<
  string,
  { label: string; color: string; description: string }
> = {
  success: {
    label: 'Success',
    color: 'rgba(34, 197, 94, 0.75)',
    description: 'Task completed or partially completed',
  },
  task_failure: {
    label: 'Task Failure',
    color: 'rgba(239, 68, 68, 0.75)',
    description: 'Connected but task could not be completed',
  },
  connection_failure: {
    label: 'Connection Failure',
    color: 'rgba(245, 158, 11, 0.75)',
    description: 'Could not reach the recipient (busy, no answer, voicemail)',
  },
  early_termination: {
    label: 'Early Termination',
    color: 'rgba(168, 85, 247, 0.75)',
    description: 'Recipient hung up or refused to engage',
  },
  audio_technical: {
    label: 'Audio / Technical',
    color: 'rgba(14, 165, 233, 0.75)',
    description: 'Technical issue with audio or the bridge',
  },
  other: {
    label: 'Other',
    color: 'rgba(100, 116, 139, 0.75)',
    description: 'Uncategorized or unknown outcome',
  },
  unassessed: {
    label: 'Unassessed',
    color: 'rgba(203, 213, 225, 0.6)',
    description: 'Calls not yet assessed by the specialist',
  },
};

const CATEGORY_GROUP_ORDER = [
  'success',
  'task_failure',
  'connection_failure',
  'early_termination',
  'audio_technical',
  'other',
  'unassessed',
];

const CATEGORY_LABELS: Record<string, string> = {
  carrier_rejected: 'Carrier Rejected',
  number_invalid: 'Number Invalid',
  number_busy: 'Number Busy',
  no_answer: 'No Answer',
  voicemail_detected: 'Voicemail',
  call_timeout: 'Call Timeout',
  recipient_hung_up_immediately: 'Hung Up Immediately',
  recipient_refused_ai: 'Refused AI',
  wrong_person: 'Wrong Person',
  wrong_number: 'Wrong Number',
  ivr_failure: 'IVR Failure',
  recipient_inaudible: 'Recipient Inaudible',
  agent_inaudible: 'Agent Inaudible',
  bridge_crashed: 'Bridge Crashed',
  realtime_api_error: 'Realtime API Error',
  task_success: 'Task Success',
  task_partial: 'Task Partial',
  task_infeasible: 'Task Infeasible',
  task_deferred: 'Task Deferred',
  task_refused: 'Task Refused',
  agent_error: 'Agent Error',
  escalated_to_human: 'Escalated to Human',
  other: 'Other',
};

interface AiCallsViewProps {
  data: AiCallsStatsResponse;
}

const AiCallsView: FC<AiCallsViewProps> = ({ data }) => {
  const labels = data.days.map(formatLabel);
  const { totals } = data;

  // ── Stacked bar: category groups per day ─────────────────────────────────
  const stackedData = {
    labels,
    datasets: CATEGORY_GROUP_ORDER.map((key) => ({
      label: CATEGORY_GROUP_META[key].label,
      data: data.categoryGroupSeries[key] ?? [],
      backgroundColor: CATEGORY_GROUP_META[key].color,
      borderRadius: 2,
      barPercentage: 0.8,
      stack: 'outcomes',
    })),
  };

  const stackedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          font: { family: 'Geist, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: {
        display: true,
        text: 'Call Outcomes per Day',
        font: { family: 'Geist, sans-serif', size: 14, weight: 600 as const },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#888',
          precision: 0,
        },
      },
    },
  };

  // ── Response latency histogram ───────────────────────────────────────
  const latencies = data.responseLatencies;
  const bucketSize = 250; // ms
  const maxBucket = Math.max(3000, ...latencies);
  const bucketCount = Math.ceil(maxBucket / bucketSize);
  const bucketCounts = new Array(bucketCount).fill(0);
  for (const ms of latencies) {
    const idx = Math.min(Math.floor(ms / bucketSize), bucketCount - 1);
    bucketCounts[idx]++;
  }
  const histogramLabels = bucketCounts.map(
    (_, i) =>
      `${((i * bucketSize) / 1000).toFixed(1)}–${(((i + 1) * bucketSize) / 1000).toFixed(1)}s`,
  );
  const histogramData = {
    labels: histogramLabels,
    datasets: [
      {
        label: 'Calls',
        data: bucketCounts,
        backgroundColor: 'rgba(59, 130, 246, 0.65)',
        borderRadius: 3,
        barPercentage: 0.9,
      },
    ],
  };
  const histogramOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `First-Turn Response Latency (${latencies.length} calls)`,
        font: { family: 'Geist, sans-serif', size: 14, weight: 600 as const },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        callbacks: {
          title: (items: any) => items[0]?.label ?? '',
          label: (ctx: any) => `${ctx.raw} call${ctx.raw === 1 ? '' : 's'}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        title: {
          display: true,
          text: 'Time after recipient stops speaking',
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#666',
        },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 10 },
          color: '#888',
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        title: {
          display: true,
          text: 'Number of calls',
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#666',
        },
        ticks: {
          font: { family: 'Geist, sans-serif', size: 11 },
          color: '#888',
          precision: 0,
        },
      },
    },
  };

  // Compute median/p90 latency for summary
  const sortedLatencies = [...latencies].sort((a, b) => a - b);
  const medianLatency =
    sortedLatencies.length > 0
      ? sortedLatencies[Math.floor(sortedLatencies.length * 0.5)]
      : null;
  const p90Latency =
    sortedLatencies.length > 0
      ? sortedLatencies[Math.floor(sortedLatencies.length * 0.9)]
      : null;

  return (
    <div className='ai-calls-view'>
      {/* Summary tiles */}
      <div className='outcomes-summary'>
        <div className='summary-tile primary'>
          <div className='tile-label'>Success Rate</div>
          <div className='tile-value'>{formatPct(totals.successRate)}</div>
          <div className='tile-sub'>
            {totals.successCount} successful of {totals.assessedCalls} assessed
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Connection Rate</div>
          <div className='tile-value'>{formatPct(totals.connectionRate)}</div>
          <div className='tile-sub'>
            {totals.connectedCalls} connected of {totals.totalCalls} total
          </div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Total Calls</div>
          <div className='tile-value'>{totals.totalCalls}</div>
          <div className='tile-sub'>{totals.assessedCalls} assessed by AI</div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Response Latency (P50)</div>
          <div className='tile-value'>
            {medianLatency != null
              ? `${(medianLatency / 1000).toFixed(2)}s`
              : '—'}
          </div>
          <div className='tile-sub'>
            P90:{' '}
            {p90Latency != null ? `${(p90Latency / 1000).toFixed(2)}s` : '—'}
            {' · '}
            {latencies.length} samples
          </div>
        </div>
      </div>

      {/* Stacked bar: outcomes per day */}
      <div className='chart-card wide outcomes-chart'>
        <Bar data={stackedData} options={stackedOptions} />
      </div>

      {/* Response latency histogram */}
      {latencies.length > 0 && (
        <div className='chart-card wide outcomes-chart'>
          <Bar data={histogramData} options={histogramOptions} />
        </div>
      )}

      {/* Assessment breakdown table */}
      {data.assessmentCategories.length > 0 && (
        <div className='outcomes-breakdown'>
          <div className='breakdown-title'>
            Detailed Assessment Categories ({totals.assessedCalls} assessed
            calls)
          </div>
          <table className='outcomes-table'>
            <thead>
              <tr>
                <th>Category</th>
                <th>Count</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {data.assessmentCategories.map((row) => (
                <tr key={row.category}>
                  <td>{CATEGORY_LABELS[row.category] ?? row.category}</td>
                  <td>{row.count}</td>
                  <td>
                    {totals.assessedCalls > 0
                      ? `${((row.count / totals.assessedCalls) * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Email routing view ───────────────────────────────────────────────────

interface EmailRoutingViewProps {
  data: NonNullable<StatsResponse['emailRouting']>;
  /** Optional — older backends without the cost block omit this. */
  cost?: StatsResponse['emailRoutingCost'];
}

/**
 * Friendlier display labels for the server-emitted bucket names.
 *
 * The server emits canonical bucket strings like `'subscription_match (user)'`
 * that match the data model 1:1. The pie chart legend reads better with
 * sentence-case labels, so we map known buckets here. Unknown buckets
 * (future code paths) fall through to the raw bucket name so analytics
 * keep working even when the FE is out of date.
 */
const EMAIL_ROUTING_BUCKET_LABELS: Record<string, string> = {
  filtered: 'Filtered (spam / promo / receipt)',
  out_of_credits: 'Out of credits',
  'subscription_match (user)': 'Subscription match — user inbox',
  'subscription_match (agent)': 'Subscription match — agent inbox',
  no_match_drop: 'No match — silent drop',
  'no_match_drop (user)': 'No match — silent drop (user)',
  'no_match_drop (agent)': 'No match — silent drop (agent)',
  fallback_routed: 'Agent fallback — routed to thread',
  fallback_artifact: 'Agent fallback — artifact created',
  uncategorized: 'Uncategorized',
};

const labelForBucket = (bucket: string): string =>
  EMAIL_ROUTING_BUCKET_LABELS[bucket] ?? bucket;

/**
 * Pie-chart triplet showing the share of inbound emails by routing
 * outcome over the past month / week / day. Mirrors the trigger-cost
 * pie layout in `CostsView` so the two tabs feel familiar.
 */
const EmailRoutingView: FC<EmailRoutingViewProps> = ({ data, cost }) => {
  const windows = [
    { label: 'Past Month', slices: data.month },
    { label: 'Past Week', slices: data.week },
    { label: 'Past Day', slices: data.day },
  ] as const;

  // Aggregate totals for the summary tiles.
  const monthTotal = data.month.reduce((s, r) => s + r.count, 0);
  const weekTotal = data.week.reduce((s, r) => s + r.count, 0);
  const dayTotal = data.day.reduce((s, r) => s + r.count, 0);

  // ── handleEmail cost: today vs same-time avg comparison ──────────────
  // Delta is calculated against the prior-days average so positive
  // values mean "today is more expensive than usual by this point".
  // We deliberately leave the delta uncoloured when prior data is too
  // thin to be meaningful (no prior days, or zero average) — a fake
  // "+infinity%" is worse than just showing the raw numbers.
  const today = cost?.todayVsAverage.today ?? 0;
  const priorAvg = cost?.todayVsAverage.priorAverage ?? 0;
  const priorDays = cost?.todayVsAverage.priorDays ?? 0;
  const hasComparable = priorDays > 0 && priorAvg > 0;
  const deltaPct = hasComparable ? ((today - priorAvg) / priorAvg) * 100 : null;
  const deltaColor =
    deltaPct === null
      ? '#888'
      : deltaPct > 25
        ? '#dc2626'
        : deltaPct > 10
          ? '#f59e0b'
          : deltaPct < -10
            ? '#16a34a'
            : '#555';

  // Daily cost line chart data — last 30 days, today is the rightmost
  // (partial) point. Treat missing days as $0 (server already
  // zero-fills, but defensively coalesce here too).
  const daily = cost?.daily ?? [];

  return (
    <div className='costs-view'>
      <div className='costs-summary'>
        <div className='summary-tile'>
          <div className='tile-label'>Inbound Emails (30d)</div>
          <div className='tile-value'>{monthTotal.toLocaleString()}</div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Inbound Emails (7d)</div>
          <div className='tile-value'>{weekTotal.toLocaleString()}</div>
        </div>
        <div className='summary-tile'>
          <div className='tile-label'>Inbound Emails (1d)</div>
          <div className='tile-value'>{dayTotal.toLocaleString()}</div>
        </div>
      </div>

      {cost && (
        <>
          {/* ── handleEmail cost: today vs same-time avg ──────────── */}
          <div className='costs-summary'>
            <div className='summary-tile'>
              <div className='tile-label'>handleEmail today (so far)</div>
              <div className='tile-value'>{formatUSD(today)}</div>
            </div>
            <div className='summary-tile'>
              <div className='tile-label'>
                Avg by this time ({priorDays}d prior)
              </div>
              <div className='tile-value'>{formatUSD(priorAvg)}</div>
            </div>
            <div className='summary-tile'>
              <div className='tile-label'>Δ vs same-time average</div>
              <div className='tile-value' style={{ color: deltaColor }}>
                {deltaPct === null
                  ? '—'
                  : `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`}
              </div>
            </div>
          </div>

          {/* ── handleEmail daily cost line chart (30 days) ─────── */}
          <div className='costs-chart-card wide'>
            <Line
              data={{
                labels: daily.map((d) => formatLabel(d.date)),
                datasets: [
                  {
                    label: 'handleEmail $/day',
                    data: daily.map((d) => d.cost),
                    borderColor: 'rgba(99, 102, 241, 1)',
                    backgroundColor: 'rgba(99, 102, 241, 0.15)',
                    fill: true,
                    tension: 0.25,
                    pointRadius: 2,
                  },
                ],
              }}
              options={{
                ...lineChartOptions('handleEmail cost per day (last 30d)'),
                plugins: {
                  ...lineChartOptions('handleEmail cost per day (last 30d)')
                    .plugins,
                  tooltip: {
                    callbacks: {
                      label: (ctx: any) =>
                        `${ctx.dataset.label}: ${formatUSD(ctx.parsed.y as number)}`,
                    },
                  },
                },
                scales: {
                  ...lineChartOptions('handleEmail cost per day (last 30d)')
                    .scales,
                  y: {
                    ...lineChartOptions('handleEmail cost per day (last 30d)')
                      .scales.y,
                    ticks: {
                      ...lineChartOptions('handleEmail cost per day (last 30d)')
                        .scales.y.ticks,
                      callback: (v: any) => formatUSD(v),
                    },
                  },
                },
              }}
            />
          </div>
        </>
      )}

      <div className='costs-trigger-pies'>
        {windows.map(({ label, slices }) => {
          const total = slices.reduce((s, r) => s + r.count, 0);
          if (total === 0) {
            return (
              <div className='costs-chart-card pie-card' key={label}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#888',
                    fontFamily: 'Geist, sans-serif',
                  }}
                >
                  {label} — no emails
                </div>
              </div>
            );
          }
          return (
            <div className='costs-chart-card pie-card' key={label}>
              <Pie
                data={{
                  labels: slices.map((s) => labelForBucket(s.bucket)),
                  datasets: [
                    {
                      data: slices.map((s) => s.count),
                      backgroundColor: [
                        ...MODEL_COLORS,
                        'rgba(100, 116, 139, 0.75)',
                        'rgba(251, 146, 60, 0.75)',
                        'rgba(99, 102, 241, 0.75)',
                      ],
                      borderWidth: 1,
                      borderColor: '#fff',
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: `${label} — ${total.toLocaleString()} emails`,
                      font: {
                        family: 'Geist, sans-serif',
                        size: 14,
                        weight: 600 as const,
                      },
                      color: '#222',
                      padding: { bottom: 8 },
                    },
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        font: { family: 'Geist, sans-serif', size: 10 },
                        boxWidth: 10,
                        padding: 6,
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (ctx: any) => {
                          const val = ctx.raw as number;
                          const pct =
                            total > 0 ? ((val / total) * 100).toFixed(1) : '0';
                          return `${ctx.label}: ${val.toLocaleString()} (${pct}%)`;
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformStats;
